import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Video, Clock, Calendar } from 'lucide-react';
import { formatTime, getStatusColor, toISTDateStr } from '../../lib/utils';
import { minutesUntilStart, canInstructorStart } from '../../lib/session-time';
import { toast } from 'sonner';

const VIEWS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export default function InstructorSchedule() {
  const [sessions, setSessions] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [daySessions, setDaySessions] = useState([]);

  const getDateRange = () => {
    const d = new Date(currentDate);
    if (view === 'day') {
      return { from: toISTDateStr(d), to: toISTDateStr(d) };
    } else if (view === 'week') {
      const day = d.getDay();
      const start = new Date(d); start.setDate(d.getDate() - day);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { from: toISTDateStr(start), to: toISTDateStr(end) };
    } else {
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return { from: toISTDateStr(start), to: toISTDateStr(end) };
    }
  };

  const fetchSessions = () => {
    const { from, to } = getDateRange();
    setLoading(true);
    api.get('/api/calendar', { params: { date_from: from, date_to: to } })
      .then(res => setSessions(res.data.sessions || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSessions(); }, [currentDate, view]);

  // Poll every 30s for live updates
  useEffect(() => {
    if (!sessions.some(s => s.status === 'upcoming' || s.status === 'live')) return;
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [sessions]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'day') d.setDate(d.getDate() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const getHeaderLabel = () => {
    const d = currentDate;
    if (view === 'day') return d.toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (view === 'week') {
      const { from, to } = getDateRange();
      return `${new Date(from + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })} – ${new Date(to + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return d.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  };

  const openDaySessions = (dateStr) => {
    const ds = sessions.filter(s => s.scheduled_date === dateStr);
    setDaySessions(ds);
    setSelectedDay(dateStr);
  };

  const handleStartSession = async (sessionId) => {
    try {
      const res = await api.get(`/api/sessions/${sessionId}/link`);
      if (res.data.zoom_start_url) {
        window.open(res.data.zoom_start_url, '_blank');
        toast.info('Opening Zoom...');
      } else {
        toast.error(res.data.start_blocked_reason || 'Cannot start yet');
      }
    } catch { toast.error('Failed'); }
  };

  // ─── WEEK VIEW ───
  const renderWeek = () => {
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(currentDate);
      const day = d.getDay();
      const start = new Date(d); start.setDate(d.getDate() - day + i);
      return start;
    });
    return (
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-3">
        {weekDays.map((d, i) => {
          const dateStr = toISTDateStr(d);
          const ds = sessions.filter(s => s.scheduled_date === dateStr);
          const isToday = dateStr === toISTDateStr(new Date());
          return (
            <Card key={i} className={`rounded-2xl cursor-pointer hover:shadow-md transition-shadow ${isToday ? 'ring-2 ring-primary' : ''}`} onClick={() => ds.length > 0 && openDaySessions(dateStr)}>
              <CardContent className="pt-3 pb-3">
                <p className="text-xs text-muted-foreground text-center">{d.toLocaleDateString('en', { weekday: 'short' })}</p>
                <p className={`text-lg font-semibold text-center mb-2 ${isToday ? 'text-primary' : ''}`}>{d.getDate()}</p>
                {ds.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center">Free</p>
                ) : (
                  <div className="space-y-1">
                    {ds.map(s => (
                      <div key={s.id} className="rounded-lg border p-2 text-xs">
                        <p className="font-medium">{formatTime(s.scheduled_time)}</p>
                        <p className="text-muted-foreground truncate">{s.batch_name}</p>
                        <Badge variant="outline" className={`${getStatusColor(s.status)} text-[10px] mt-1`}>
                          {s.status === 'live' ? <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span> : s.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // ─── DAY VIEW ───
  const renderDay = () => {
    const dateStr = toISTDateStr(currentDate);
    const ds = sessions.filter(s => s.scheduled_date === dateStr);
    return (
      <div className="space-y-3">
        {ds.length === 0 ? (
          <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12">
            <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No sessions scheduled</p>
          </CardContent></Card>
        ) : ds.map(s => (
          <SessionRow key={s.id} s={s} onStart={handleStartSession} />
        ))}
      </div>
    );
  };

  // ─── MONTH VIEW ───
  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <p key={d} className="text-xs text-muted-foreground text-center font-medium py-1">{d}</p>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const ds = sessions.filter(s => s.scheduled_date === dateStr);
            const isToday = dateStr === toISTDateStr(new Date());
            const hasLive = ds.some(s => s.status === 'live');
            return (
              <div key={i} className={`rounded-lg border p-1.5 min-h-[60px] cursor-pointer hover:bg-muted/20 transition-colors ${isToday ? 'ring-2 ring-primary' : ''} ${hasLive ? 'bg-emerald-50' : ''}`} onClick={() => ds.length > 0 && openDaySessions(dateStr)}>
                <p className={`text-xs font-medium ${isToday ? 'text-primary' : ''}`}>{day}</p>
                {ds.length > 0 && (
                  <div className="mt-0.5">
                    {ds.slice(0, 2).map(s => (
                      <p key={s.id} className="text-[9px] text-muted-foreground truncate">{formatTime(s.scheduled_time)} {s.batch_name?.split(' ')[0]}</p>
                    ))}
                    {ds.length > 2 && <p className="text-[9px] text-primary">+{ds.length - 2} more</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="instructor-schedule">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">My Schedule</h1>
      </div>

      {/* View toggle + Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {VIEWS.map(v => (
            <button key={v.key} onClick={() => setView(v.key)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium min-w-[180px] text-center">{getHeaderLabel()}</span>
          <Button variant="ghost" size="icon" onClick={() => navigate(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
        </div>
      </div>

      {/* Render view */}
      {loading ? (
        <div className="grid grid-cols-7 gap-3">{Array.from({length:7}).map((_,i) => <div key={i} className="h-32 bg-muted/40 rounded-2xl animate-pulse" />)}</div>
      ) : view === 'week' ? renderWeek() : view === 'day' ? renderDay() : renderMonth()}

      {/* Day Sessions Modal */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => { if (!o) setSelectedDay(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {selectedDay && new Date(selectedDay + 'T00:00:00').toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long' })}
            </DialogTitle>
          </DialogHeader>
          {daySessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No sessions</p>
          ) : (
            <div className="space-y-2">
              {daySessions.map(s => <SessionRow key={s.id} s={s} onStart={handleStartSession} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SessionRow({ s, onStart }) {
  // V14.5 — anchor to IST so non-IST instructors (or admins) see the correct
  // start window. `minutesUntilStart` parses scheduled_date+scheduled_time
  // as IST regardless of browser TZ. Mirrors instructor Dashboard.js.
  const minsUntil = minutesUntilStart(s) ?? 9999;
  const canStart = canInstructorStart(s, 10);
  return (
    <div className="flex items-center justify-between p-3 rounded-xl border hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3">
        <div className="text-center min-w-[48px]">
          <p className="text-sm font-semibold">{formatTime(s.scheduled_time)}</p>
          <p className="text-[10px] text-muted-foreground">{s.duration_minutes}m</p>
        </div>
        <div>
          <p className="text-sm font-medium">{s.batch_name || s.title}</p>
          <Badge variant="outline" className={`text-[10px] mt-0.5 ${getStatusColor(s.status)}`}>
            {s.status === 'live' ? <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span> : s.status}
          </Badge>
        </div>
      </div>
      {s.status === 'live' ? (
        <Button size="sm" onClick={() => onStart(s.id)}><Video className="h-3.5 w-3.5 mr-1" />Rejoin</Button>
      ) : s.status === 'upcoming' && canStart ? (
        <Button size="sm" onClick={() => onStart(s.id)}><Video className="h-3.5 w-3.5 mr-1" />Start</Button>
      ) : s.status === 'upcoming' && minsUntil <= 20 ? (
        <Button size="sm" disabled className="opacity-40">Starting soon</Button>
      ) : null}
    </div>
  );
}
